import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { MENU } from '../../config/menu';
import { useAuth } from '../../context/AuthContext';
import { COMPANY_NAME } from '../../config';

const isItemActive = (item, pathname) => {
  if (item.to && pathname.startsWith(item.to)) return true;
  if (item.children && item.children.some((c) => pathname.startsWith(c.to))) return true;
  return false;
};

export default function Sidebar({ open, collapsed, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [openGroups, setOpenGroups] = useState({});

  const visibleMenu = useMemo(() => {
    const result = [];
    let currentGroup = null;
    let currentGroupItems = [];

    const flushGroup = () => {
      if (currentGroup && currentGroupItems.length > 0) {
        result.push({ group: currentGroup });
        result.push(...currentGroupItems);
      } else if (!currentGroup && currentGroupItems.length > 0) {
        result.push(...currentGroupItems);
      }
      currentGroup = null;
      currentGroupItems = [];
    };

    for (const item of MENU) {
      if (item.group) {
        flushGroup();
        currentGroup = item.group;
        continue;
      }
      if (item.permission && !hasPermission(item.permission)) continue;
      currentGroupItems.push(item);
    }
    flushGroup();

    return result;
  }, [hasPermission]);

  useEffect(() => {
    const active = MENU.find(
      (m) => m.children && m.children.some((c) => location.pathname.startsWith(c.to))
    );
    if (active && active.label) {
      setOpenGroups((prev) => ({ ...prev, [active.label]: true }));
    }
  }, [location.pathname]);

  const toggle = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const renderItem = (item) => {
    if (item.group) {
      return <div key={item.group} className="nav-group-label">{item.group}</div>;
    }
    const active = isItemActive(item, location.pathname);

    if (item.children) {
      const expanded = openGroups[item.label];
      const isOpen = expanded !== undefined ? expanded : active;
      const firstChild = item.children[0];
      return (
        <div key={item.label}>
          <button
            className={`nav-item ${active ? 'active' : ''}`}
            title={collapsed ? item.label : undefined}
            onClick={() => (collapsed ? navigate(firstChild.to) : toggle(item.label))}
          >
            <span className="nav-icon"><item.icon /></span>
            {!collapsed && <span>{item.label}</span>}
            {!collapsed && <FiChevronRight className={`nav-arrow ${isOpen ? 'open' : ''}`} />}
          </button>
          {!collapsed && isOpen && (
            <div className="nav-sub">
              {item.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className={`nav-sub-item ${location.pathname === c.to ? 'active' : ''}`}
                >
                  <span className="dot" />
                  {c.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        className={`nav-item ${active ? 'active' : ''}`}
        title={collapsed ? item.label : undefined}
        onClick={onClose}
      >
        <span className="nav-icon"><item.icon /></span>
        {!collapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <aside className={`sidebar ${open ? 'open' : ''}`}>
      <div className="sidebar-logo">
        <img src="/logo.png" alt="Logo" className="logo-badge" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'contain' }} />
        {!collapsed && (
          <div>
            <div className="logo-name">{COMPANY_NAME}</div>
            <div className="logo-sub">ERP Suite</div>
          </div>
        )}
      </div>
      <nav className="sidebar-nav">{visibleMenu.map(renderItem)}</nav>
    </aside>
  );
}
